// PlanRenameModal — name a single uploaded plan, with queue awareness.
//
// v0.13.1 / v0.29.0 — initial (lived inline in ProjectDetailScreen).
// v0.45.25 — extracted to its own file so PlanScreen can also render
//            it during multi-file uploads. Brett: "I just uploaded 2
//            drawings and it took me straight to the plan page and
//            skipped the loading two files page where i could rename
//            them."
//
// Props:
//   file        — the File object being renamed
//   defaultName — pre-populated nickname (filename without extension)
//   uploading   — disable inputs + buttons while a POST is in flight
//   queueIndex  — 0-based index of this file within a multi-file queue
//   queueTotal  — total queue length (>1 toggles "Plan X of N" pill +
//                 Skip button)
//   onCancel    — kill the entire queue
//   onSkip      — drop this one and advance (null = don't show button)
//   onConfirm   — fires with the chosen nickname; parent runs the POST

import React, { useEffect, useRef, useState } from 'react';
import { theme, input, btnPrimary, btnSecondary } from '../theme';

export default function PlanRenameModal({
  file, defaultName, uploading,
  queueIndex = 0, queueTotal = 1,
  onCancel, onSkip, onConfirm,
}) {
  const [nickname, setNickname] = useState(defaultName);
  const nicknameRef = useRef(null);

  // Reset nickname + re-focus when the queue advances to a new file.
  // Without this the next file's modal would still show the previous
  // file's nickname.
  useEffect(() => {
    setNickname(defaultName);
    if (nicknameRef.current) {
      nicknameRef.current.focus();
      nicknameRef.current.select();
    }
  }, [file, defaultName]);

  const hasQueue = queueTotal > 1;

  return (
    <div
      onClick={() => { if (!uploading) onCancel(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 100, padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme.surface, width: 560, maxWidth: '100%',
          borderRadius: 10, padding: 22,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
          <h3 style={{ margin: 0, fontSize: 18 }}>Name this plan</h3>
          {hasQueue && (
            <span style={{
              fontSize: 11, fontWeight: 700, color: theme.brand,
              background: theme.bg, border: `1px solid ${theme.brand}`,
              padding: '2px 8px', borderRadius: 999,
            }}>Plan {queueIndex + 1} of {queueTotal}</span>
          )}
        </div>
        <div style={{ fontSize: 12, color: theme.textLight, marginBottom: 14 }}>
          {hasQueue
            ? `Give this plan a short name. ${queueTotal - queueIndex - 1} more queued after this one.`
            : 'Give the plan a short, friendly name. The original filename stays as a backup reference.'}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <Label>Filename</Label>
            <div style={{
              ...input,
              background: theme.bg,
              color: theme.textLight,
              cursor: 'not-allowed',
              fontFamily: 'monospace',
              fontSize: 12,
              padding: '8px 10px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }} title={file && file.name}>{file && file.name}</div>
          </div>
          <div>
            <Label>Nickname</Label>
            <input
              ref={nicknameRef}
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter'  && nickname.trim() && !uploading) onConfirm(nickname);
                if (e.key === 'Escape' && !uploading)                    onCancel();
              }}
              style={input}
              placeholder="e.g. Ground floor"
              maxLength={200}
              disabled={uploading}
            />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 18 }}>
          <div>
            {onSkip && (
              <button
                onClick={onSkip}
                disabled={uploading}
                style={{ ...btnSecondary, fontSize: 12 }}
                title="Skip this file and move on to the next"
              >Skip this one →</button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onCancel} disabled={uploading} style={btnSecondary}>
              {hasQueue ? `Cancel (${queueTotal} pending)` : 'Cancel'}
            </button>
            <button
              onClick={() => onConfirm(nickname)}
              disabled={uploading || !nickname.trim()}
              style={{ ...btnPrimary, opacity: (uploading || !nickname.trim()) ? 0.5 : 1 }}
            >
              {uploading ? 'Loading plan…' : (hasQueue ? 'Load & next' : 'Load plan')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Label({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 600, color: theme.textLight,
      textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 5,
    }}>{children}</div>
  );
}
